/**
 * A preview's knowledge refresh (#5142 step 4; K10, K18; #5083 decisions 112 and 145): the data
 * product's preview file snapshot, `POST /v1/{preview}/files/snapshot` `{ cursor? }`, paged to its
 * end. Each page makes one range of the preview's files equal its production origin's (path,
 * content, content type, metadata), removes the preview's own files production does not hold, and
 * answers what it did and where the next page starts. `wayai knowledge refresh-preview` and the
 * console's "Refresh preview knowledge" both reach it through the `/api/data` proxy.
 *
 * WayAI cannot import the data product, so the answer it reads is declared here, a copy of the data
 * product's `fileSnapshotAnswerSchema` (`apps/rekor/shared/src/toolset/answer-shapes.ts`).
 * `scripts/seam-guards/check-toolset-connector-contract.ts` compares the two, so a drift on either
 * side fails CI: move both in one change.
 */

import { z } from 'zod';
import { getEndpointConfig } from '../rate-limits';
import { DATA_PROXY_PREFIX } from './route-paths';

/** One page's answer: what it did to the preview, and the cursor of the next page (`null` after the last). */
export const rekorFileSnapshotAnswerSchema = z.object({
  data: z.object({
    next_cursor: z.string().nullable(),
    /** Files the page created or replaced on the preview. */
    written: z.number().int(),
    /** The preview's own files, ones production does not hold, the page removed. */
    removed: z.number().int(),
    /** Files the page found already equal to production's. */
    unchanged: z.number().int(),
  }),
});

export type RekorFileSnapshotPage = z.infer<typeof rekorFileSnapshotAnswerSchema>['data'];

/** What a whole run did: its pages, and their counts summed. */
export interface FileSnapshotRun {
  pages: number;
  written: number;
  removed: number;
  unchanged: number;
}

/**
 * Pages one run follows before refusing: a page holds up to 100 files, so far more than any
 * knowledge base, and few enough that a server answering fresh cursors forever stops.
 */
export const FILE_SNAPSHOT_MAX_PAGES = 10_000;

/**
 * Times one page is sent while it conflicts: the data product refuses a page whose preview file
 * changed while its bytes were being copied, and asks for the same cursor again.
 */
export const FILE_SNAPSHOT_CONFLICT_ATTEMPTS = 3;

/**
 * The spacing between pages that keeps a refresh at four fifths of the person's `/api/data` budget
 * a minute (every console read and data command shares it): the rate `wayai knowledge refresh-preview`
 * keeps, and the console's refresh too.
 */
export const FILE_SNAPSHOT_PAGE_SPACING_MS = Math.ceil(
  60_000 / Math.max(1, Math.floor(((getEndpointConfig(`${DATA_PROXY_PREFIX}/files/snapshot`)?.userLimit ?? 60) * 4) / 5)),
);

/** The status of that refusal, which `isConflict` recognises on each caller's own error class. */
export const FILE_SNAPSHOT_CONFLICT_STATUS = 409;

/** What `runFileSnapshot` is given: how to send a page, which refusal to send again, and who hears of each page. */
export interface FileSnapshotRunOptions {
  sendPage: (cursor: string | undefined) => Promise<unknown>;
  isConflict: (error: unknown) => boolean;
  onPage?: (page: RekorFileSnapshotPage, index: number) => void;
}

/** Whether a run changed the preview: wrote or removed a file. */
export function fileSnapshotChanged(run: FileSnapshotRun): boolean {
  return run.written + run.removed > 0;
}

/**
 * Page a preview file snapshot to its end. `sendPage` sends one page (`undefined` = the first) and
 * returns its parsed body; a page `isConflict` says was refused for a concurrent change is sent again
 * with the same cursor, and any other refusal ends the run, thrown to the caller as the server
 * answered it. A re-run is safe at any point: a page that finds the preview equal writes nothing.
 */
export async function runFileSnapshot(opts: FileSnapshotRunOptions): Promise<FileSnapshotRun> {
  const run: FileSnapshotRun = { pages: 0, written: 0, removed: 0, unchanged: 0 };
  const seen = new Set<string>();
  let cursor: string | undefined;
  while (run.pages < FILE_SNAPSHOT_MAX_PAGES) {
    const page = await sendWithRetry(opts, cursor);
    run.pages++;
    run.written += page.written;
    run.removed += page.removed;
    run.unchanged += page.unchanged;
    opts.onPage?.(page, run.pages);
    if (page.next_cursor === null) return run;
    if (seen.has(page.next_cursor)) {
      throw new Error(`The preview file snapshot repeated its cursor after page ${run.pages}, so it would never end.`);
    }
    seen.add(page.next_cursor);
    cursor = page.next_cursor;
  }
  throw new Error(`The preview file snapshot did not end after ${FILE_SNAPSHOT_MAX_PAGES} pages.`);
}

async function sendWithRetry(
  opts: Pick<FileSnapshotRunOptions, 'sendPage' | 'isConflict'>,
  cursor: string | undefined,
): Promise<RekorFileSnapshotPage> {
  for (let attempt = 1; ; attempt++) {
    let body: unknown;
    try {
      body = await opts.sendPage(cursor);
    } catch (error) {
      if (attempt < FILE_SNAPSHOT_CONFLICT_ATTEMPTS && opts.isConflict(error)) continue;
      throw error;
    }
    const parsed = rekorFileSnapshotAnswerSchema.safeParse(body);
    if (!parsed.success) {
      throw new Error('The data product answered a preview file snapshot page WayAI cannot read: nothing more was sent.');
    }
    return parsed.data.data;
  }
}
